import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../services/api';

export interface AvsjekkRecord {
  AvsjekkID: string;
  InntektslinjeID: string;
  Periode_aar: number;
  Periode_maaned: number;
  Status: 'OK' | 'Avvik' | 'Uavklart';
  Kommentar: string | null;
  Referanse: string | null;
  Utfoert_av: string | null;
  Utfoert_dato: string;
  Enhet: string;
  Ansvar: string;
  Konto: string;
  KontoNavn: string;
}

export interface AvsjekkFilters {
  page: number;
  pageSize: number;
  periode_aar: string;
  periode_maaned: string;
  status: string;
  ansvar: string;
  sortBy: string;
  sortDir: string;
}

export const defaultAvsjekkFilters: AvsjekkFilters = {
  page: 1,
  pageSize: 50,
  periode_aar: String(new Date().getFullYear()),
  periode_maaned: '',
  status: '',
  ansvar: '',
  sortBy: 'Utfoert_dato',
  sortDir: 'DESC',
};

interface AvsjekkListResponse {
  data: AvsjekkRecord[];
  pagination: { page: number; pageSize: number; totalCount: number };
}

function buildQueryString(filters: AvsjekkFilters): string {
  const params = new URLSearchParams();
  params.set('page', String(filters.page));
  params.set('pageSize', String(filters.pageSize));
  if (filters.periode_aar) params.set('periode_aar', filters.periode_aar);
  if (filters.periode_maaned) params.set('periode_maaned', filters.periode_maaned);
  if (filters.status) params.set('status', filters.status);
  if (filters.ansvar) params.set('ansvar', filters.ansvar);
  params.set('sortBy', filters.sortBy);
  params.set('sortDir', filters.sortDir);
  return params.toString();
}

export function useAvsjekkList(filters: AvsjekkFilters) {
  return useQuery({
    queryKey: ['avsjekk', filters],
    queryFn: () => apiGet<AvsjekkListResponse>(`/avsjekk?${buildQueryString(filters)}`),
    placeholderData: (prev) => prev,
  });
}

export interface CreateAvsjekkInput {
  inntektslinjeID: string;
  periode_aar: number;
  periode_maaned: number;
  status: string;
  kommentar?: string;
  referanse?: string;
}

export function useCreateAvsjekk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAvsjekkInput) =>
      apiPost('/avsjekk', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['avsjekk'] });
    },
  });
}
